import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'model/Api';
import { User } from 'model/User';
import { useRole } from 'model/Role';
import { LineRelation } from 'model/Relation';
import { generalStyle } from 'helper/Style';
import { convertDate, convertUserName } from 'helper/Converter';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import Modal, { modalStyle } from 'component/part/Modal';
import H2 from 'component/part/H2';

const style = {
  formArea: {
    display: 'grid',
    gridTemplateColumns: '30rem auto',
    justifyContent: 'flex-start',
    alignItems: 'top',
    gap: '4rem',
  },
};

const LineRelationEdit: React.FC = () => {
  const api = useApi();
  const { getListAsync: getRoleListAsync } = useRole();
  const navigate = useNavigate();
  const urlParams = useParams<{ id: string }>();
  const relationId = parseInt(urlParams.id || '');
  const [relation, setRelation] = useState<LineRelation>();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<User['id']>();
  const [confirmStatus, setConfirmStatus] = useState(0);
  const [resultStatus, setResultStatus] = useState(0);

  const onSubmit = async () => {
    if (relationId && selectedUserId) {
      await api.lineRelation.relate(relationId, selectedUserId);
      setConfirmStatus(0);
      setResultStatus(1);
    }
  };

  const onUnderstoodResult = () => {
    setResultStatus(0);
    if (resultStatus === 2) {
      navigate('/line-relation/list');
    }
  };

  useEffect(() => {
    (async () => {
      if (relationId) {
        const relationResponse = await api.lineRelation.get(relationId);
        if (relationResponse) {
          setRelation(relationResponse);
        }
      }
    })();
  }, [relationId]);

  useEffect(() => {
    (async () => {
      const roles = await getRoleListAsync();
      const assignTargetRoleIds = roles.flatMap((role) => role.permissions.workAssign ? role.id : []);
      const userResponse = await api.user.getsByOwn({ roleId: assignTargetRoleIds, with: ['relation'] });
      setUsers(userResponse);
    })();
  }, [relationId]);

  return <Layout>
    <BackLink link='/line-relation/list' label='メンバー一覧'></BackLink>
    <H2>メンバー情報</H2>
    <div css={style.formArea}>
      <div css={generalStyle.form}>
        <FormContent label='受信日時'>
          <div css={generalStyle.item()}>{convertDate(relation?.createDatetime || 0)}</div>
        </FormContent>
        <FormContent label='LINEユーザー名'>
          <div css={generalStyle.item()}>{relation?.platformUserName || ''}</div>
        </FormContent>
        <div css={generalStyle.formRow}>
          <button
            disabled={!selectedUserId}
            onClick={() => selectedUserId && setConfirmStatus(1)}
            css={generalStyle.button('normal', !selectedUserId)}
          >
            連携
          </button>
        </div>
      </div>
      <div css={generalStyle.list(3)}>
        <label css={generalStyle.listRow}>
          <div></div>
          <div>連携先メンバー</div>
          <div>連携済</div>
        </label>
        {users.map((user, index) => (
          <label css={generalStyle.listRow} key={index}>
            <div>
            <input
              type='radio'
              checked={user.id === selectedUserId}
              onChange={() => setSelectedUserId(user.id)}
            />
            </div>
            <div>{convertUserName(user)}</div>
            <div>{user.relation ? '◯' : '×'}</div>
          </label>
        ))}
      </div>
    </div>
    <Modal display={!!confirmStatus} onHide={() => setConfirmStatus(0)}>
      <div>メンバーにLINEアカウントを連携させます</div>
      <div css={modalStyle.buttonArea}>
        <button onClick={onSubmit} css={generalStyle.button()}>連携</button>
        <button onClick={() => setConfirmStatus(0)} css={generalStyle.button('warning')}>キャンセル</button>
      </div>
    </Modal>
    <Modal display={!!resultStatus} onHide={() => setResultStatus(0)}>
      <div>連携が完了しました</div>
      <div css={modalStyle.buttonArea}>
        <button onClick={onUnderstoodResult} css={generalStyle.button()}>OK</button>
      </div>
    </Modal>
  </Layout>;
};

export default LineRelationEdit;
